import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { CONT_COLLECTIONS } from "./collections";
import { BASE_PCGE } from "./pcgeDefaults";

export { BASE_PCGE };

const db = getFirestore(firebaseApp);

const cleanText = (v) => (v == null ? "" : String(v).trim());
const normalizeCode = (v) => cleanText(v).replace(/[^\d]/g, "");

const REQUIRED_TIPOS = ["COSTO_DIRECTO", "GASTO_ADMINISTRATIVO", "GASTO_VENTAS", "ACTIVO_FIJO"];

const pickFirstCuentaByPrefix = (cuentas, prefijos = []) => {
  for (const prefijo of prefijos) {
    const found = cuentas.find((x) => cleanText(x.codigo).startsWith(prefijo));
    if (found?.codigo) return cleanText(found.codigo);
  }
  return "";
};

export const inicializarPlanCuentasBase = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) return;
  const existing = await obtenerPlanCuentasPorEmpresa(ruc);
  const existingCodes = new Set(existing.map((c) => normalizeCode(c.codigo)));

  const missing = BASE_PCGE.filter((c) => !existingCodes.has(normalizeCode(c.codigo)));
  if (missing.length === 0) return;

  const tasks = missing.map((c) =>
    setDoc(
      doc(db, CONT_COLLECTIONS.PLAN_CUENTAS, `${ruc}_${c.codigo}`),
      {
        ruc_empresa: ruc,
        codigo: c.codigo,
        nombre: c.nombre,
        clase: c.clase,
        naturaleza: c.naturaleza,
        nivel: String(c.codigo.length),
        estado: "ACTIVO",
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      },
      { merge: true }
    )
  );
  await Promise.all(tasks);
};

export const obtenerPlanCuentasPorEmpresa = async (rucEmpresa) => {
  const q = query(
    collection(db, CONT_COLLECTIONS.PLAN_CUENTAS),
    where("ruc_empresa", "==", cleanText(rucEmpresa)),
    where("estado", "==", "ACTIVO")
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => cleanText(a.codigo).localeCompare(cleanText(b.codigo)));
};

export const guardarCuentaContable = async ({ rucEmpresa, cuenta }) => {
  const ruc = cleanText(rucEmpresa);
  const codigo = normalizeCode(cuenta.codigo);
  if (!ruc || !codigo) throw new Error("Cuenta inválida.");
  const id = `${ruc}_${codigo}`;
  const vigenciaDesde = cleanText(cuenta.vigencia_desde || cuenta.vigenciaDesde || "");
  const vigenciaHasta = cleanText(cuenta.vigencia_hasta || cuenta.vigenciaHasta || "");
  await setDoc(
    doc(db, CONT_COLLECTIONS.PLAN_CUENTAS, id),
    {
      ruc_empresa: ruc,
      codigo,
      nombre: cleanText(cuenta.nombre),
      clase: cleanText(cuenta.clase || codigo[0]),
      naturaleza: cleanText(cuenta.naturaleza || "GASTO"),
      nivel: cleanText(cuenta.nivel || String(codigo.length)),
      vigencia_desde: vigenciaDesde || null,
      vigencia_hasta: vigenciaHasta || null,
      estado: "ACTIVO",
      updated_at: serverTimestamp(),
      created_at: serverTimestamp(),
      auditoria: arrayUnion({
        accion: "GUARDAR_CUENTA",
        fecha: new Date().toISOString(),
      }),
    },
    { merge: true }
  );
  return id;
};

export const inactivarCuentaContable = async ({ rucEmpresa, codigo }) => {
  const ruc = cleanText(rucEmpresa);
  const cod = normalizeCode(codigo);
  if (!ruc || !cod) throw new Error("Cuenta inválida.");
  await setDoc(
    doc(db, CONT_COLLECTIONS.PLAN_CUENTAS, `${ruc}_${cod}`),
    { estado: "INACTIVO", updated_at: serverTimestamp() },
    { merge: true }
  );
};

export const obtenerReglasAsientoPorEmpresa = async (rucEmpresa) => {
  const q = query(collection(db, CONT_COLLECTIONS.REGLAS_ASIENTO), where("ruc_empresa", "==", cleanText(rucEmpresa)));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => cleanText(a.tipo_gasto).localeCompare(cleanText(b.tipo_gasto)));
};

export const guardarReglaAsiento = async ({ rucEmpresa, regla }) => {
  const ruc = cleanText(rucEmpresa);
  const tipo = cleanText(regla.tipo_gasto || regla.tipoGasto);
  const cuentaDebe = normalizeCode(regla.cuenta_debe);
  const cuentaHaber = normalizeCode(regla.cuenta_haber || "421201");
  const cuentaDestino = normalizeCode(regla.cuenta_destino);
  if (!ruc || !tipo) throw new Error("Regla inválida.");

  const plan = await obtenerPlanCuentasPorEmpresa(ruc);
  const exists = (codigo) => !codigo || plan.some((x) => normalizeCode(x.codigo) === codigo);
  if (!exists(cuentaDebe)) throw new Error(`La cuenta debe ${cuentaDebe} no existe en cont_plan_cuentas.`);
  if (!exists(cuentaHaber)) throw new Error(`La cuenta haber ${cuentaHaber} no existe en cont_plan_cuentas.`);
  if (!exists(cuentaDestino)) throw new Error(`La cuenta destino ${cuentaDestino} no existe en cont_plan_cuentas.`);

  const id = `${ruc}_${tipo}`;
  await setDoc(
    doc(db, CONT_COLLECTIONS.REGLAS_ASIENTO, id),
    {
      ruc_empresa: ruc,
      tipo_gasto: tipo,
      cuenta_debe: cuentaDebe,
      cuenta_haber: cuentaHaber,
      cuenta_destino: cuentaDestino,
      vigencia_desde: cleanText(regla.vigencia_desde || regla.vigenciaDesde || "") || null,
      vigencia_hasta: cleanText(regla.vigencia_hasta || regla.vigenciaHasta || "") || null,
      estado: "ACTIVO",
      updated_at: serverTimestamp(),
      created_at: serverTimestamp(),
      auditoria: arrayUnion({
        accion: "GUARDAR_REGLA",
        fecha: new Date().toISOString(),
      }),
    },
    { merge: true }
  );
  return id;
};

export const importarConfiguracionPlanillas = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) return { cuentasImportadas: 0, reglasImportadas: 0 };

  const configDoc = await getDoc(doc(db, "pl_config_contable", ruc));
  if (!configDoc.exists()) {
    return { cuentasImportadas: 0, reglasImportadas: 0 };
  }

  const cfg = configDoc.data() || {};
  const cuentas = cfg.cuentas || {};
  let cuentasImportadas = 0;

  const entries = Object.entries(cuentas);
  await Promise.all(
    entries.map(async ([key, cuenta]) => {
      const codigo = cleanText(cuenta?.codigo).replace(/[^\d]/g, "");
      const nombre = cleanText(cuenta?.descripcion || key).toUpperCase();
      if (!codigo || !nombre) return;
      await setDoc(
        doc(db, CONT_COLLECTIONS.PLAN_CUENTAS, `${ruc}_${codigo}`),
        {
          ruc_empresa: ruc,
          codigo,
          nombre,
          clase: codigo[0],
          naturaleza: codigo[0] === "1" ? "ACTIVO" : codigo[0] === "4" ? "PASIVO" : codigo[0] === "6" ? "GASTO" : "OTRO",
          nivel: cleanText(cuenta?.nivel || String(codigo.length)),
          origen: "PLANILLAS",
          estado: "ACTIVO",
          updated_at: serverTimestamp(),
          created_at: serverTimestamp(),
        },
        { merge: true }
      );
      cuentasImportadas += 1;
    })
  );

  const reglas = [
    {
      tipo_gasto: "COSTO_DIRECTO",
      cuenta_debe: cleanText(cuentas?.sueldos?.codigo || "601101"),
      cuenta_haber: cleanText(cuentas?.remuneraciones_pagar?.codigo || "421201"),
      cuenta_destino: "941101",
    },
    {
      tipo_gasto: "GASTO_ADMINISTRATIVO",
      cuenta_debe: cleanText(cuentas?.bonificaciones?.codigo || "631101"),
      cuenta_haber: cleanText(cuentas?.remuneraciones_pagar?.codigo || "421201"),
      cuenta_destino: "951101",
    },
    {
      tipo_gasto: "GASTO_VENTAS",
      cuenta_debe: cleanText(cuentas?.horas_extras?.codigo || "641101"),
      cuenta_haber: cleanText(cuentas?.remuneraciones_pagar?.codigo || "421201"),
      cuenta_destino: "951201",
    },
    {
      tipo_gasto: "ACTIVO_FIJO",
      cuenta_debe: "331101",
      cuenta_haber: cleanText(cuentas?.bancos?.codigo || "421201"),
      cuenta_destino: "",
    },
  ];

  await Promise.all(
    reglas.map((regla) =>
      setDoc(
        doc(db, CONT_COLLECTIONS.REGLAS_ASIENTO, `${ruc}_${regla.tipo_gasto}`),
        {
          ruc_empresa: ruc,
          tipo_gasto: regla.tipo_gasto,
          cuenta_debe: regla.cuenta_debe,
          cuenta_haber: regla.cuenta_haber,
          cuenta_destino: regla.cuenta_destino,
          estado: "ACTIVO",
          origen: "PLANILLAS",
          updated_at: serverTimestamp(),
          created_at: serverTimestamp(),
        },
        { merge: true }
      )
    )
  );

  return { cuentasImportadas, reglasImportadas: reglas.length };
};

export const diagnosticarConfiguracionContable = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) return null;

  const [cuentas, reglas] = await Promise.all([
    obtenerPlanCuentasPorEmpresa(ruc),
    obtenerReglasAsientoPorEmpresa(ruc),
  ]);
  const reglasByTipo = reglas.reduce((acc, r) => {
    acc[cleanText(r.tipo_gasto)] = r;
    return acc;
  }, {});

  const cuentasMinimas = [
    "101101",
    "104101",
    "12121",
    "401111",
    "421201",
    "701101",
    "941101",
    "951101",
    "951201",
  ];
  const cuentasFaltantes = cuentasMinimas.filter((codigo) => !cuentas.some((c) => cleanText(c.codigo) === codigo));
  const reglasFaltantes = REQUIRED_TIPOS.filter((tipo) => !reglasByTipo[tipo] || cleanText(reglasByTipo[tipo].estado) !== "ACTIVO");
  const reglasIncompletas = REQUIRED_TIPOS.filter((tipo) => {
    const regla = reglasByTipo[tipo];
    if (!regla) return false;
    return !cleanText(regla.cuenta_debe) || !cleanText(regla.cuenta_haber);
  });

  return {
    cuentasTotal: cuentas.length,
    reglasTotal: reglas.length,
    cuentasFaltantes,
    reglasFaltantes,
    reglasIncompletas,
    ok: cuentasFaltantes.length === 0 && reglasFaltantes.length === 0 && reglasIncompletas.length === 0,
  };
};

export const autocompletarConfiguracionContableEmpresa = async (rucEmpresa) => {
  const ruc = cleanText(rucEmpresa);
  if (!ruc) return { cuentasCreadas: 0, reglasCreadas: 0 };

  await inicializarPlanCuentasBase(ruc);
  await importarConfiguracionPlanillas(ruc);
  const [cuentas] = await Promise.all([obtenerPlanCuentasPorEmpresa(ruc)]);

  const ensureCuentas = BASE_PCGE;

  let cuentasCreadas = 0;
  await Promise.all(
    ensureCuentas.map(async (c) => {
      const existe = cuentas.some((x) => cleanText(x.codigo) === c.codigo);
      if (existe) return;
      await guardarCuentaContable({ rucEmpresa: ruc, cuenta: c });
      cuentasCreadas += 1;
    })
  );

  const cuentaDebeGasto = pickFirstCuentaByPrefix(cuentas, ["62", "60", "63", "64"]) || "601101";
  const cuentaDebeAdmin = pickFirstCuentaByPrefix(cuentas, ["63", "62", "60"]) || "631101";
  const cuentaDebeVentas = pickFirstCuentaByPrefix(cuentas, ["64", "62", "60"]) || "641101";
  const cuentaHaber = pickFirstCuentaByPrefix(cuentas, ["41", "42", "40"]) || "421201";

  const reglasBase = [
    { tipo_gasto: "COSTO_DIRECTO", cuenta_debe: cuentaDebeGasto, cuenta_haber: cuentaHaber, cuenta_destino: "941101" },
    { tipo_gasto: "GASTO_ADMINISTRATIVO", cuenta_debe: cuentaDebeAdmin, cuenta_haber: cuentaHaber, cuenta_destino: "951101" },
    { tipo_gasto: "GASTO_VENTAS", cuenta_debe: cuentaDebeVentas, cuenta_haber: cuentaHaber, cuenta_destino: "951201" },
    { tipo_gasto: "ACTIVO_FIJO", cuenta_debe: "331101", cuenta_haber: cuentaHaber, cuenta_destino: "" },
  ];

  let reglasCreadas = 0;
  await Promise.all(
    reglasBase.map(async (regla) => {
      await guardarReglaAsiento({ rucEmpresa: ruc, regla });
      reglasCreadas += 1;
    })
  );

  return { cuentasCreadas, reglasCreadas };
};
